import { describeCause } from "./reportError";
import type { ErrorEvent, EventHint } from "@sentry/react";

/**
 * Repairs the title of a Sentry event synthesised from `console.error`.
 * Issue #1273, the other half of #1255.
 *
 * ── The problem ────────────────────────────────────────────────────────────
 *
 * `captureConsoleIntegration` (`8c0b454`) makes every `console.error` a Sentry
 * event, which is right — a caught PostgREST error logged to the console used
 * to produce nothing at all, and that blind spot let #1210 run broken for
 * seven weeks.
 *
 * But the integration builds the title by joining the console arguments with a
 * space, and a plain object stringifies to `[object Object]`:
 *
 *     console.error("[X] failed", { code: "23505", message: "dupe" })
 *
 *       event.logger  = "console"
 *       event.message = "[X] failed [object Object]"   ← titled and GROUPED on
 *       event.extra   = { arguments: ["[X] failed", { code: …, message: … }] }
 *
 * Two costs, and the second is the one that hides:
 *
 *   1. `#bugs` shows `[object Object]` where the message should be.
 *   2. **Grouping breaks.** Sentry groups on the title, so every distinct
 *      failure from one call site collapses into a single issue — and the
 *      field that would separate them is exactly the one that got stringified
 *      away. A spike in one underlying error looks like no change at all.
 *
 * ── Why this is a beforeSend and not 179 edits ─────────────────────────────
 *
 * Measured on `main` at c597f11: **202** `console.error` calls in `src/`
 * (excluding tests), **179** of which pass a non-string second argument,
 * across **47** files.
 *
 * The data is not lost — `extra.arguments` still holds the real objects — so
 * this is repairable after the fact, in one place. Editing 179 call sites
 * would fix today and nothing after it: the 180th lands untagged next week.
 * It would also mean a 47-file logging churn through `KrogerPricing`,
 * `TripPlannerTool` and `ChatMessages`, which is a lot of blast radius for a
 * title.
 *
 * ⚠️ It only ever rewrites a title that is ALREADY useless, from data already
 * on the event. It adds nothing, drops nothing, and touches no other event.
 */

/** The marker the integration sets. Verified against a real BrowserClient. */
const CONSOLE_LOGGER = "console";

/** Present only when the join produced an unreadable title. */
const USELESS = "[object Object]";

/**
 * Rebuilds a console event's message from its raw arguments.
 *
 * Exported for tests. Returns `null` when there is nothing to repair, so the
 * caller can leave the event strictly untouched rather than reassigning an
 * identical string — "did not touch it" and "rewrote it to the same value" are
 * different claims, and the tests assert the first.
 */
export function repairedTitle(event: ErrorEvent): string | null {
  if (event.logger !== CONSOLE_LOGGER) return null;
  if (typeof event.message !== "string" || !event.message.includes(USELESS)) return null;

  const args = (event.extra as { arguments?: unknown } | undefined)?.arguments;
  if (!Array.isArray(args) || args.length === 0) return null;

  const rebuilt = args
    .map((arg) => (typeof arg === "string" ? arg : describeCause(arg)))
    .join(" ")
    .trim();

  // If flattening did not actually help — an argument whose own description is
  // still useless — leave the event alone rather than swapping one bad title
  // for another and making it look handled.
  if (!rebuilt || rebuilt.includes(USELESS)) return null;
  return rebuilt;
}

/**
 * `beforeSend` hook. Returns the event, repaired where it needs repairing.
 *
 * Never drops an event: this is a titling fix, and swallowing a report here
 * would recreate the exact blind spot `8c0b454` closed.
 */
export function repairConsoleEventTitle(event: ErrorEvent, _hint?: EventHint): ErrorEvent {
  const rebuilt = repairedTitle(event);
  if (rebuilt === null) return event;
  return { ...event, message: rebuilt };
}
